import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { ENV, PROJECT_ID_PLACEHOLDER } from './common/constants';
import 'react-tooltip/dist/react-tooltip.css';
import './common/scss/app.scss';
import AppPage from 'modules/app/App.tsx';
import FullLayout from './Layouts/FullLayout.tsx';
import ErrorPage, { ErrorType } from 'common/components/errorPages/ErrorPage.tsx';
import DynamicModule from 'modules/dynamicModule/DynamicModule.tsx';
import AuthorizedRoute from './routes/AuthorizedRoute.tsx';
import PrivacyPolicy from 'common/components/legal/PrivacyPolicy.tsx';
import TermsOfUse from 'common/components/legal/TermsOfUse.tsx';
import CookieNotice from 'common/components/legal/CookieNotice.tsx';
import { ErrorBoundary } from 'react-error-boundary';
import { userRoutes } from './routes/routeConfigs/user.routes.tsx';
import { Provider } from 'react-redux';
import store, { persistor } from 'common/store/index.ts';
import { APP_ROOT, IDP_LOGIN_ROUTE, MODULE_ROUTES } from 'routes/routes.const.ts';
import { PersistGate } from 'redux-persist/integration/react';
import { Spinner } from 'common/components/loader/Spinner.tsx';
import { ModuleProjectProvider } from 'common/contexts/useModuleProjectContext.tsx';
import { ToastContainer } from 'common/components/notification/ToastContainer.tsx';
import ProtectedRoute from 'routes/ProtectedRoute.tsx';
import PlanningDynamicModule from 'modules/planning/components/PlanningDynamicRoutes/PlanningDynamicRoutes.tsx';
import { useParams } from 'react-router-dom';
import LegacyAppRoute from 'routes/LegacyAppRoute.tsx';
import usePowerBiReport from 'common/hooks/usePowerBiReport.ts';
import PowerBiModule from 'modules/powerBi/PowerBiModule.tsx';
import { captureTelemetryException, initTelemetry } from 'common/utils/telemetry.ts';
import IdpInitiatedLogin from 'common/auth/IdpInitiatedLogin.tsx';

// GlitchTip telemetry (#134) - no-op when VITE_GLITCHTIP_DSN is unset.
initTelemetry();

// Global override to make certain event listeners passive
(function () {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  const passiveEvents = ['touchstart', 'touchmove', 'touchend', 'wheel', 'mousewheel'];

  EventTarget.prototype.addEventListener = function (type, listener, options) {
    if (passiveEvents.includes(type)) {
      // Convert to options object if it's a boolean or undefined
      if (typeof options === 'boolean') {
        options = { capture: options, passive: true };
      } else if (typeof options === 'undefined') {
        options = { passive: true };
      } else if (typeof options === 'object' && options !== null) {
        // If options is already an object, add passive unless explicitly set to false
        if (!('passive' in options)) {
          options = { ...options, passive: true };
        }
      }
    }

    return originalAddEventListener.call(this, type, listener, options);
  };
})();

const ModuleRouter = () => {
  const { module } = useParams<{ module: string }>();
  const { isPowerBiReport } = usePowerBiReport();

  if (isPowerBiReport) {
    return <PowerBiModule />;
  }

  switch (module) {
    case 'planning':
      return <PlanningDynamicModule />;
    default:
      return <DynamicModule />;
  }
};

const router = createBrowserRouter([
  {
    path: APP_ROOT,
    element: (
      <ModuleProjectProvider>
        <AppPage />
        <ToastContainer />
      </ModuleProjectProvider>
    ),
    children: [
      {
        path: IDP_LOGIN_ROUTE,
        element: <IdpInitiatedLogin />,
      },
      {
        path: MODULE_ROUTES.privacyPolicy,
        element: <PrivacyPolicy />,
      },
      {
        path: MODULE_ROUTES.terms,
        element: <TermsOfUse />,
      },
      {
        path: MODULE_ROUTES.cookies,
        element: <CookieNotice />,
      },
      ...userRoutes,
      {
        path: `/app/:module?/:page?/:subPage?/:tab?`,
        // #141 removed the sibling AuthorizedRoute from this legacy Veeva entry:
        // it translates only, leaving authorization to the modern project route.
        // #151 completes the same invariant for cold Auth0 root returns; #150
        // records the incident timeline and why both entry paths matter.
        element: <LegacyAppRoute />,
      },
      {
        path: `/:module/${PROJECT_ID_PLACEHOLDER}?/:page?/:subPage?/:tab?`,
        element: (
          <AuthorizedRoute>
            <ProtectedRoute>
              <ModuleRouter />
            </ProtectedRoute>
          </AuthorizedRoute>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <FullLayout>
        <ErrorPage errorType={ErrorType.NOT_FOUND} />
      </FullLayout>
    ),
  },
]);

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <ErrorBoundary fallback={<ErrorPage />} onError={captureTelemetryException}>
    <Auth0Provider
      domain={ENV.VITE_AUTH0_DOMAIN}
      clientId={ENV.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: ENV.VITE_AUTH0_AUDIENCE,
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <Provider store={store}>
        <PersistGate loading={<Spinner />} persistor={persistor}>
          <RouterProvider router={router} />
        </PersistGate>
      </Provider>
    </Auth0Provider>
  </ErrorBoundary>
);
